import { getQueryClient } from "@/app/providers/QueryProvider"
import type { QueryKey } from "@tanstack/react-query"

export const queryKeyBuilder = <TParts extends readonly unknown[]>(baseKey: string, ...parts: TParts) => {
  return [baseKey, ...parts] as const
}

type GetBlockingFreshQueryDataOptions<TQueryFnData, TQueryKey extends QueryKey> = {
  queryKey: TQueryKey
  queryFn: () => Promise<TQueryFnData>
  staleTime?: number
  onFetchStart?: () => void
  onFetchEnd?: () => void
}

/**
 * This function checks if there is fresh data in the cache for the given query key.
 * If there is, it returns the cached data. If not, it fetches new data using the provided query function.
 * The main difference between this function and the way TanstackQuery normally handles cache
 * is that this function will always return fresh data if the cached data is stale,
 * while useQuery returns stale data while it fetches fresh data in the background.
 * It also allows you to specify callbacks for when the fetch starts and ends, which can be used to show loading indicators.
 */
export const getBlockingFreshQueryData = async <TQueryFnData, TQueryKey extends QueryKey>({
  queryKey,
  queryFn,
  staleTime = 10 * 1000, // default stale time is 10 seconds
  onFetchStart,
  onFetchEnd,
}: GetBlockingFreshQueryDataOptions<TQueryFnData, TQueryKey>) => {
  const queryClient = getQueryClient()

  const hasFreshQueryDataInCache = () => {
    const queryState = queryClient.getQueryState(queryKey)

    if (queryState?.isInvalidated) {
      return false
    }

    if (!queryState?.dataUpdatedAt) {
      return false
    }

    return Date.now() - queryState.dataUpdatedAt < staleTime
  }

  if (hasFreshQueryDataInCache()) {
    return queryClient.getQueryData<TQueryFnData>(queryKey)!
  }

  try {
    onFetchStart?.()

    return await queryClient.fetchQuery({
      queryKey,
      queryFn,
      staleTime,
    })
  } finally {
    onFetchEnd?.()
  }
}
